import { Dialog, Transition } from '@headlessui/react';
import Image from 'next/future/image';
import { Fragment } from 'react';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import xIcon from '@/public/icons/account/close-teal.svg';
import searchIcon from '@/public/icons/collection/usps/search.svg';
import returnsIcon from '@/public/icons/collection/usps/returns.svg';
import shieldIcon from '@/public/icons/collection/usps/shield.svg';
import batteryIcon from '@/public/icons/collection/usps/battery.svg';
import cleanIcon from '@/public/icons/collection/usps/clean.svg';

const data = [
  {
    icon: searchIcon,
    title: 'Certified Quality',
    text: "Our experts verify each item's authenticity, functionality, and condition to ensure you receive a product you'll love.",
  },
  {
    icon: returnsIcon,
    title: 'Free Returns Up To 30 Days',
    text: "Changed your mind or not happy with the product you received? Return your purchase for any reason for up to 30 days. We'll provide a prepaid return label free of charge.",
  },
  {
    icon: shieldIcon,
    title: '1-Year Warranty',
    text: "In the unlikely event of a functional issue, Reebelo's warranty has you covered for up to 12-months. We'll repair or replace your product at no cost, with free shipping.",
  },
  {
    icon: batteryIcon,
    title: 'Battery Health Guarantee',
    text: "On applicable products, all batteries come with at least 80% capacity for reliable daily use, and we often offer options to upgrade to 90% or 100%—these capacities are guaranteed. You're covered for 12 months against battery failure and overheating, and in the unlikely event that your battery drains within the first 3 months more than 10% below the battery tier you selected, you can return your device for a full refund. After the coverage period, we'll connect you with trusted repair partners for discounted battery replacements.",
  },
  {
    icon: cleanIcon,
    title: 'Wiped And Sanitized',
    text: 'Professionally reset to factory settings on applicable products. All products are thoroughly cleaned, and sanitized. No leftover data, no dust, no surprises.',
  },
];

const Header = ({ hideModal }: { hideModal: () => void }) => (
  <Dialog.Title as="div" className="w-full bg-[#92F7FA] text-gray-700">
    <div className="relative flex w-full items-center justify-center">
      <Image
        src={getEdgeUrl('images/collections/product-page/usps/usps-banner.png')}
        height={428}
        width={900}
        alt="Sustainable Banner"
        className="h-auto w-full"
        quality={100}
      />
      <div className="absolute h-full w-full max-w-[400px] p-5">
        <div className="flex h-full w-full flex-col items-center justify-center text-center">
          <h2 className="text-lg font-bold sm:text-xl">Verified Refurbished</h2>
          <p>
            Every device is quality-tested, data-wiped, professionally cleaned,
            and backed by our warranty for up to 12 months.
          </p>
        </div>
      </div>
    </div>
    <button
      onClick={hideModal}
      type="button"
      className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-700 outline-none transition duration-150 ease-in-out hover:scale-110 focus:outline-none"
    >
      <Image
        src={xIcon}
        alt="Close"
        className="h-2.5 w-2.5"
        height={30}
        width={30}
      />
    </button>
  </Dialog.Title>
);
const Content = () => (
  <div className="flex h-full flex-col justify-between p-5 text-sm">
    <div className="mt-5 space-y-10">
      {data.map((item) => (
        <div className="flex gap-3">
          <Image src={item.icon} alt="Icon" className="h-7 w-7 sm:h-8 sm:w-8" />
          <div>
            <h2 className="text-base font-bold">{item.title}</h2>
            <p className="">{item.text}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const UspsModal = ({
  showModal,
  hideModal,
}: {
  showModal: boolean;
  hideModal: () => void;
}) => (
  <Transition.Root show={showModal} as={Fragment}>
    <Dialog as="div" className="relative z-50" onClose={hideModal}>
      <Transition.Child
        as={Fragment}
        enter="ease-in-out duration-500"
        enterFrom="opacity-0"
        enterTo="opacity-30"
        leave="ease-in-out duration-500"
        leaveFrom="opacity-30"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-700" onClick={hideModal} />
      </Transition.Child>
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full md:w-[500px]">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto relative w-full">
                <div className="relative flex h-full flex-col overflow-y-auto bg-white">
                  <Header hideModal={hideModal} />
                  <Content />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </div>
    </Dialog>
  </Transition.Root>
);

export default UspsModal;
